import { useState, useRef, useEffect } from 'react'
import { Container, Row, Col } from '@ui/wrapper'
import {
  SectionWrapper,
  StyledText,
  TabsWrapper,
  Card,
  CardHeader,
  CardFooter,
  Menu,
  MenuButton,
  MenuItem,
  RadioWrapper
} from './style'
import SectionTitle from '@ui/section-title'
import Tabs, { Tab } from '@ui/tabs'
import Accordion from '@ui/accordion'
import BasicToast from '@components/toast'
import MenuInfo from '@components/menu-info'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBars } from '@fortawesome/pro-solid-svg-icons/faBars'
import { faImage } from '@fortawesome/pro-regular-svg-icons/faImage'
import { faCloudArrowUp } from '@fortawesome/pro-regular-svg-icons/faCloudArrowUp'
import { PieChart } from './charts'
import Form from 'react-bootstrap/Form'
import { Spinner } from 'react-bootstrap'
import './style.css'
import useBrowserFingerprint from '@hooks/use-browser-fingerprint'
import { usePageContext } from '@context/PageContext'
import { texts } from '@constants/trend-des-monats'

const textDelimiter = '|'

const logInterval = (totalIntervals, start, end) => {
  const x = (Math.log(end) - Math.log(start)) / totalIntervals
  const factor = Math.exp(x)
  const result = [start]

  for (let i = 1; i < totalIntervals; i++) {
    result.push(result[result.length - 1] * factor)
  }
  result.push(end)
  return result
}

const getColorArray = (steps, color, min, max) => {
  color = color.replace(')', ',')
  const colorArray = logInterval(steps, min, max)
    .map((e) => color + e.toString() + ')')
    .reverse()

  return colorArray
}

function useOutsideAlerter(ref, hideMenu) {
  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        hideMenu()
      }
    }
    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [ref])
}

const OwnCard = ({
  title,
  subtitle,
  data,
  colorObject,
  seriesName,
  localeTexts
}) => {
  const [showMenu, setShowMenu] = useState(false)
  const wrapperRef = useRef(null)
  const chartRef = useRef(null)

  const hideMenu = () => {
    setShowMenu(false)
  }

  const resultData = Object.keys(data.results).map((e) => ({
    name: e,
    y: data.results[e]
  }))

  resultData.sort((a, b) => {
    if (a.y < b.y) return 1
    if (a.y > b.y) return -1
    return 0
  })

  const colorArray = getColorArray(
    resultData.length,
    colorObject.color,
    colorObject.min,
    colorObject.max
  )
  const coloredData = resultData.map((e, i) => ({ ...e, color: colorArray[i] }))

  useOutsideAlerter(wrapperRef, hideMenu)

  const [titleText, infoText = ''] = title.split(textDelimiter)

  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <Card>
        <div
          style={{
            display: 'flex',
            justifyContent: 'right',
            alignItems: 'right',
            position: 'relative'
          }}
        >
          <MenuButton onClick={() => setShowMenu(true)}>
            <FontAwesomeIcon icon={faBars} />
          </MenuButton>
          {showMenu && (
            <Menu ref={wrapperRef}>
              <MenuItem
                onClick={() =>
                  chartRef.current?.exportChartLocal({ type: 'image/png' })
                }
              >
                <FontAwesomeIcon
                  style={{ paddingRight: 10, color: 'rgb(181 90 48)' }}
                  icon={faImage}
                />
                {localeTexts.download.png}
              </MenuItem>
              <MenuItem
                onClick={() =>
                  chartRef.current?.exportChartLocal({ type: 'image/jpeg' })
                }
              >
                <FontAwesomeIcon
                  style={{ paddingRight: 10, color: 'rgb(181 90 48)' }}
                  icon={faImage}
                />
                {localeTexts.download.jpeg}
              </MenuItem>
              <MenuItem
                onClick={() =>
                  chartRef.current?.exportChartLocal({ type: 'image/svg+xml' })
                }
              >
                <FontAwesomeIcon
                  style={{ paddingRight: 10, color: 'rgb(181 90 48)' }}
                  icon={faImage}
                />
                {localeTexts.download.svg}
              </MenuItem>
              {/* <MenuItem onClick={() => chartRef.current?.fullscreen.open()}><i style={{ paddingRight: 10, color: 'rgb(181 90 48)' }} className='fas fa-expand-alt' />Vollbild-Modus</MenuItem> */}
            </Menu>
          )}
        </div>
        <Row style={{ position: 'relative' }}>
          <CardHeader>
            <span style={{ whiteSpace: 'normal', marginRight: '10px' }}>
              {titleText}
            </span>
            {infoText && <MenuInfo>{infoText}</MenuInfo>}
          </CardHeader>
        </Row>
        <Row>
          <Col lg={12}>
            <PieChart
              chartRef={chartRef}
              seriesName={seriesName}
              title={subtitle.split(textDelimiter)[0]}
              subtitle={subtitle.split(textDelimiter)[1]}
              exportTitle={data.title || titleText}
              data={coloredData}
            />
          </Col>
        </Row>
        <CardFooter>
          {data.uploadDate ? (
            <>
              <FontAwesomeIcon icon={faCloudArrowUp} /> {data.uploadDate},{' '}
              {localeTexts.asOf}: {data.lastUpdate}
            </>
          ) : (
            <>
              {localeTexts.asOf}:{' '}
              {new Date().toLocaleDateString('de-DE', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
              })}{' '}
            </>
          )}
          <span style={{ float: 'right' }}>{localeTexts.source}</span>
        </CardFooter>
      </Card>
    </div>
  )
}

const OwnPollCard = ({
  title,
  data,
  onSetShowToast,
  onSubmit,
  localeTexts
}) => {
  const optionTexts = data.texts.slice(1, data.texts.length)

  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <Card>
        <Row style={{ position: 'relative' }}>
          <CardHeader>
            <span style={{ whiteSpace: 'normal' }}>{title}</span>
          </CardHeader>
        </Row>
        <Row>
          <Col lg={12}>
            <RadioWrapper>
              <Form>
                {optionTexts.map((e, i) => {
                  const splitText = e.content?.split(textDelimiter)

                  return (
                    <Form.Check
                      type="radio"
                      name="trend"
                      key={`trend-${i}`}
                      className="mb-2"
                    >
                      <Form.Check.Input
                        type="radio"
                        value={i}
                        name="trend"
                        id={`trend-${i}`}
                      />
                      <Form.Check.Label htmlFor={`trend-${i}`}>
                        {splitText[0]}
                        <br />
                        {splitText.length > 1 && (
                          <span className="text-muted opacity-75">
                            {splitText[1]}
                          </span>
                        )}
                      </Form.Check.Label>
                    </Form.Check>
                  )
                })}
                <button
                  onClick={(event) => {
                    event.preventDefault()
                    if (document.querySelector('[name=trend]:checked')) {
                      onSubmit(event)
                    } else {
                      onSetShowToast()
                    }
                  }}
                  type="submit"
                  className="btn btn-outline-primary"
                >
                  {localeTexts.submit}
                </button>
              </Form>
            </RadioWrapper>
          </Col>
        </Row>
      </Card>
    </div>
  )
}

const PollWrapper = ({ loading, error, children, localeTexts }) => {
  const style = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px',
    color: '#00408a',
    marginBottom: '50px'
  }

  if (loading) {
    return (
      <div style={style}>
        <Spinner animation="border" />
      </div>
    )
  }

  if (error) {
    return (
      <div style={style}>
        <span>{localeTexts.noData}</span>
      </div>
    )
  }

  return children
}

const convertDBResultsToChartData = (data, pollOptions) => {
  const results = {}
  for (let i = 0; i < pollOptions.length; i++) {
    const result = data.results.find((e) => e.vote === i)
    results[pollOptions[i]] = result ? parseInt(result.count) : 0
  }
  return results
}

const TrendDesMonatsArea = ({ data, dataPoll, ...rest }) => {
  const [loading, setLoading] = useState(true)

  const [votingAllowed, setVotingAllowed] = useState(false)
  const [pollData, setPollData] = useState({ id: '', name: '', active: null })

  const [currentMonthData, setCurrentMonthData] = useState([])
  const [previousMonthData, setPreviousMonthData] = useState([])

  const toastRef = useRef(null)

  const userFingerprint = useBrowserFingerprint()

  const { locale } = usePageContext()

  const localeTexts = texts[locale] || texts.de

  const pollTexts = dataPoll?.texts
  const pollOptions = pollTexts
    ?.slice(1, pollTexts.length)
    .map((e) => e.content)
  const hasPollData = Boolean(pollTexts?.length)

  useEffect(() => {
    if (userFingerprint) {
      const fetchData = async () => {
        try {
          const response = await fetch('/api/trend-des-monats', {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'x-fingerprint': userFingerprint
            }
          })
          if (!response.ok) {
            throw new Error('Network response was not ok')
          }
          const resData = await response.json()
          if (resData.active && !resData.voted) {
            setVotingAllowed(true)
          }

          if (resData.results) {
            const results = convertDBResultsToChartData(resData, pollOptions)
            setCurrentMonthData({
              total: resData.total,
              results
            })
          }
          setPollData({
            id: resData.id,
            name: resData.name,
            active: resData.active
          })
        } catch (error) {
          setPollData((prevState) => ({ ...prevState, active: false }))
          console.error('Error fetching poll:', error)
        }
      }
      fetchData()
    }
  }, [userFingerprint])

  useEffect(() => {
    if (pollData.active === null) return
    const fetchData = async () => {
      try {
        const res = await fetch(
          `/api/data/trend-des-monats?active=${pollData.active}&locale=${locale}`
        )
        const r = await res.json()
        const current = r.current
        const previous = r.previous

        if (!current && !previous) {
          throw new Error('No data available')
        }

        try {
          if (current && current.length > 0) {
            const c = current[0]
            const d = (c.lastUpdate || c.exportDate).split('T')[0].split('-')
            const lastUpdate = `${d[2]}.${d[1]}.${d[0]}`
            setCurrentMonthData({
              title: c.title,
              total: c.total,
              results: c.results,
              lastUpdate,
              uploadDate: current[1],
              final: c.final
            })
          }
        } catch (error) {
          console.error('Error setting current month data:', error)
        }

        try {
          const p = previous[0]
          const d = (p.lastUpdate || p.exportDate).split('T')[0].split('-')
          const lastUpdate = `${d[2]}.${d[1]}.${d[0]}`
          setPreviousMonthData({
            title: p.title,
            total: p.total,
            results: p.results,
            lastUpdate,
            uploadDate: previous[1]
          })
        } catch (error) {
          console.error('Error setting previous month data:', error)
        }
      } catch (error) {
        console.error('Error fetching poll:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [pollData])

  const onSubmitHandler = async (event) => {
    event.preventDefault()
    const vote = document.querySelector('[name=trend]:checked').id.split('-')[1]
    try {
      const response = await fetch('/api/trend-des-monats', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Fingerprint': userFingerprint
        },
        body: JSON.stringify({
          id: pollData.id,
          description: pollData.name,
          vote
        })
      })

      if (!response.ok) {
        throw new Error('Network response was not ok')
      }

      const resData = await response.json()

      const results = convertDBResultsToChartData(resData, pollOptions)

      setVotingAllowed(false)
      setCurrentMonthData((prevState) => ({
        ...prevState,
        total: resData.total,
        results
      }))
      // setTotalPollVotes(resData.total)
    } catch (error) {
      console.error('Error voting:', error)
    }
  }

  return (
    <SectionWrapper {...rest}>
      <Container>
        {data?.texts?.[0] && (
          <StyledText
            dangerouslySetInnerHTML={{
              __html: data.texts[0].content
            }}
          />
        )}
        <TabsWrapper>
          <Tabs defaultActiveKey="current_trend">
            <Tab eventKey="current_trend" title={localeTexts.titleCurrent}>
              <PollWrapper
                loading={loading}
                error={
                  !currentMonthData.results && !(votingAllowed && hasPollData)
                }
                localeTexts={localeTexts}
              >
                {votingAllowed && hasPollData && (
                  <OwnPollCard
                    title={dataPoll?.texts?.[0].content.split(textDelimiter)[0]}
                    data={dataPoll}
                    localeTexts={localeTexts}
                    onSubmit={onSubmitHandler}
                    onSetShowToast={() => {
                      toastRef?.current?.setShow(true)
                    }}
                  />
                )}
                {!votingAllowed && currentMonthData.results && (
                  <OwnCard
                    title={
                      currentMonthData.title || dataPoll?.texts?.[0].content
                    }
                    data={currentMonthData}
                    subtitle={`${pollData.active === false && currentMonthData.final !== false ? localeTexts.endResults : localeTexts.interimResults}|(${currentMonthData?.total || 0} ${localeTexts.participants})`}
                    colorObject={{ color: 'rgb(87,120,169)', min: 0.1, max: 1 }}
                    seriesName="Trend"
                    localeTexts={localeTexts}
                  />
                )}
              </PollWrapper>
            </Tab>
            {previousMonthData.results && (
              <Tab eventKey="last_trend" title={localeTexts.titlePrevious}>
                <PollWrapper
                  loading={loading}
                  error={!previousMonthData.results}
                  localeTexts={localeTexts}
                >
                  {previousMonthData.results && (
                    <OwnCard
                      title={previousMonthData.title}
                      data={previousMonthData}
                      subtitle={`${localeTexts.endResults}|(${previousMonthData?.total || 0} ${localeTexts.participants})`}
                      colorObject={{
                        color: 'rgb(87,120,169)',
                        min: 0.1,
                        max: 1
                      }}
                      seriesName="Trend"
                      localeTexts={localeTexts}
                    />
                  )}
                </PollWrapper>
              </Tab>
            )}
          </Tabs>
        </TabsWrapper>
        {data?.section_title && (
          <SectionTitle
            mt={['40px', null, '60px']}
            mb={['30px', null, '40px']}
            subtitle={data.section_title?.subtitle}
            title={data.section_title?.title}
          />
        )}
        {data?.items?.length > 1 && (
          <StyledText>
            <Accordion iconAtEnd={false} data={data.items} />
          </StyledText>
        )}
      </Container>
      <BasicToast
        ref={toastRef}
        title={localeTexts.errorToastTitle}
        body={localeTexts.errorToastBody}
      />
    </SectionWrapper>
  )
}

export default TrendDesMonatsArea
